import React, {useEffect, useRef, useState} from 'react';
import {
    AlertCircle,
    Check,
    Info, List,
    Save,
    X
} from 'lucide-react';
import {MiniOrderDetails} from "../MiniOrderDetails.tsx";
import {
    ErrorCount,
    ErrorFeedback,
    getErrorCount,
    getErrorFeedback, scrollToSection,
} from "./dyanimcFormUtil.tsx";
import {FormToolbarErrorDisplay} from "./fields/FormToolbarErrorDisplay.tsx";
import {useFormActions, useFormMethods, useFormStatic} from "./DyanmicFormContext.tsx";
import {FieldErrors, useFormState} from "react-hook-form";
import {debounce} from "lodash";
import {FormComponent, FormValues} from "./DynamicFormTypes.ts";
import {HeaderInfo, HeaderInfoBar} from "./HeaderInfoBar.tsx";

interface FormToolbarProps {
    manualTrigger: number;
    jumpToList: FormComponent[];
    headerInfo?: HeaderInfo;
}

// Tab content wrapper component
interface TabContentProps {
    title: string;
    icon: React.ReactNode;
    borderColor: string;
    textColor: string;
    onClose: () => void;
    children: React.ReactNode;
}


const TabContent: React.FC<TabContentProps> = ({
                                                   title,
                                                   icon,
                                                   borderColor,
                                                   textColor,
                                                   onClose,
                                                   children
                                               }) => {
    return (
        <div className={`bg-white p-3 rounded-lg border ${borderColor} max-h-80 overflow-y-auto`}>
            <div className="flex justify-between items-center mb-2">
                <h3 className={`font-medium ${textColor} flex items-center`}>
                    {icon}
                    <span className="ml-1">{title}</span>
                </h3>
                <button
                    onClick={onClose}
                    className={`${textColor} hover:opacity-70`}
                    type="button"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
            {children}
        </div>
    );
};

const FormToolbarV2Renderer: React.FC<FormToolbarProps> = ({manualTrigger, jumpToList, headerInfo = undefined}) => {
    const { formMethods } = useFormMethods();
    const { flattenedSchemaMap, formPermissions } = useFormStatic();

    const { isValidating, errors } = useFormState({
        control: formMethods.control
    });
    const [activeTab, setActiveTab] = useState<'errors' | 'details' | 'sections' | null>(null);

    const [errorFeedback, setErrorFeedback] = useState<ErrorFeedback>();
    const [errorCount, setErrorCount] = useState<ErrorCount>();
    const debouncedUpdateRef = useRef<ReturnType<typeof debounce> | undefined>(undefined);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        debouncedUpdateRef.current = debounce((currentErrors: FieldErrors<FormValues>, currentSchemaMap: Map<string, FormComponent>, activeTab: string) => {
            //error feedback is expensive so we only do it when the errors tab is open
            if (activeTab === 'errors') {
                console.debug("errors tab open, we need to get the full feedback");
                const newErrors = getErrorFeedback(currentErrors, currentSchemaMap);
                setErrorFeedback(newErrors);
                setErrorCount({
                    warnings: newErrors?.nonNestedWarnings?.length || 0,
                    errors: newErrors?.nonNestedErrors?.length || 0,
                    total: newErrors?.totalErrorsCount || 0
                })
                return;
            }
            //otherwise we just need the counts (much cheaper)
            console.debug("errors tab not open, just getting counts");
            const errorCount = getErrorCount(currentErrors);
            setErrorCount(errorCount);
        }, 1000)

        return () => {
            console.debug("un subscribing from form state changes in form toolbar");
            debouncedUpdateRef.current?.cancel();
        }
    }, []);

    useEffect(() => {
        if (!isValidating) {
            debouncedUpdateRef.current?.(errors, flattenedSchemaMap, activeTab);
        }
    }, [manualTrigger, isValidating, errors, activeTab]);


    // Active tab state
    const {metaData} = useFormStatic()
    const {triggerValidation} = useFormMethods();
    const  { saveProgress } = useFormActions()

    const save = () => {
        setSaving(true);
        saveProgress().catch(error => {
            console.log("Error trying to auto save", error)
        }).finally(() => {

            setSaving(false);
        })
    }

    useEffect(() => {
        const interval = setInterval(() => {
            save();
        }, 5 * 60 * 1000); // 5 minutes in milliseconds

        return () => {
            clearInterval(interval);// stop interval when component unmounts
        }
    }, []);


    // Get total file count (all file types)
    // const totalFileCount = generalFiles.length + (metaData?.orderDetails?.files ? metaData?.orderDetails?.files.length : 0);

    // Toggle tab function
    const toggleTab = (tab: 'errors' | 'details' | 'sections') => {
        if (activeTab === tab) {
            setActiveTab(null);
        } else {
            setActiveTab(tab);
        }
    };

    return (
        <div className="bg-gray-300 rounded-b-md h-fit shadow-lg fixed z-[40] top-16 right-0 left-0 lg:left-64">
            <div className="p-2 space-y-2">
                {/* Toolbar Title */}
                {headerInfo &&
                    <HeaderInfoBar headerInfo={headerInfo}/>
                }


                {/* Toolbar Buttons */}
                <div className="flex space-x-2">
                    {/* Jump To Sections Button */}
                    {jumpToList && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault(); // Prevent default button behavior
                                toggleTab('sections');
                            }}
                            className={`px-3 py-2 rounded-md text-sm font-medium flex items-center flex-1 justify-center
                                    ${activeTab === 'sections'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            } transition-colors`}
                        >
                            <List className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline line-clamp-1">
                                Sections
                            </span>
                        </button>
                    )}

                    {/* Check Error Button */}
                    <button
                        type="button"
                        onClick={async (e) => {
                            e.preventDefault();
                            setActiveTab(null);
                            await triggerValidation();
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center flex-1 justify-center bg-slate-100 text-slate-700 hover:bg-slate-200`}
                    >
                        <Check className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline line-clamp-1">
                            Error Check
                        </span>
                    </button>

                    {/* View Error Button */}
                    <button
                        type="button"
                        onClick={async (e) => {
                            e.preventDefault();
                            setErrorFeedback(undefined); // Clear previous feedback
                            toggleTab('errors');
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center flex-1 justify-center
                                ${activeTab === 'errors' || errorCount && errorCount.total > 0
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                    >
                        <AlertCircle className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline line-clamp-1">
                                View Errors {errorCount && errorCount.total > 0 && (
                                    <>({errorCount.total})</>
                        )}
                        </span>
                    </button>

                    {/* Order Details Button */}
                    {metaData.orderDetails && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault(); // Prevent default button behavior
                                toggleTab('details');
                            }}
                            className={`px-3 py-2 rounded-md text-sm font-medium flex items-center flex-1 justify-center
                                    ${activeTab === 'details'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            } transition-colors`}
                        >
                            <Info className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline line-clamp-1">
                                Details
                            </span>
                        </button>
                    )}

                    {/* Save Button */}
                    {formPermissions.canSave && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                setSaving(true);
                                setActiveTab(null);
                                saveProgress().finally(() => setSaving(false));
                            }}
                            className={`px-3 py-2 rounded-md text-sm font-medium flex items-center flex-1 justify-center bg-slate-100 text-slate-700 hover:bg-slate-200
                                    ${saving ? 'animate-pulse bg-slate-500' : ''}
                                    transition-colors`}
                            disabled={saving}
                        >
                            <Save className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline line-clamp-1">
                                {saving ? 'Saving…' : 'Save'}
                            </span>
                        </button>
                    )}
                </div>

                {/* Jump To Sections Tab */}
                {activeTab === 'sections' && (
                    <TabContent
                        title="Jump to Sections"
                        icon={<AlertCircle className="h-4 w-4 mr-1" />}
                        borderColor="border-blue-100"
                        textColor="text-blue-700"
                        onClose={() => setActiveTab(null)}
                    >
                        {jumpToList && jumpToList.length > 0 && (
                            <div className="ml-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                                {jumpToList.map(component => (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault(); // Prevent default button behavior
                                            scrollToSection(component.fieldKey);
                                            setTimeout(() => setActiveTab(null), 300);
                                        }}
                                        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center flex-1 justify-center bg-slate-100 text-slate-700 hover:bg-blue-100 hover:text-blue-700`}
                                    >
                                        {component.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </TabContent>
                )}

                {/* Errors List Tab */}
                {activeTab === 'errors' && (
                    <TabContent
                        title="Form Errors"
                        icon={<AlertCircle className="h-4 w-4 mr-1" />}
                        borderColor="border-red-100"
                        textColor="text-red-700"
                        onClose={() => setActiveTab(null)}
                    >
                        {!errorFeedback ? (
                            <div className="flex items-center text-sm text-slate-600">
                                Checking for errors...
                            </div>
                        ) : errorFeedback.totalErrorsCount > 0 ? (
                            <FormToolbarErrorDisplay errorFeedback={errorFeedback}/>
                        ) : (
                            <div className="flex items-center text-sm text-green-600">
                                <Check className="h-4 w-4 mr-1" />
                                No errors found in the form
                            </div>
                        )}
                    </TabContent>
                )}

                {/* Order Details Tab*/}
                {activeTab === 'details' && metaData.orderDetails && (
                    <TabContent
                        title="Order Details"
                        icon={<Info className="h-4 w-4 mr-1" />}
                        borderColor="border-green-100"
                        textColor="text-green-700"
                        onClose={() => setActiveTab(null)}
                    >
                        {/* Replace the simple Order ID text with the MiniOrderDetails component */}
                        <div className="w-full">
                            <MiniOrderDetails orderId={metaData.orderId.toString()} />
                        </div>
                    </TabContent>
                )}
            </div>
        </div>
    );
}

export const FormToolbarV2 = React.memo(FormToolbarV2Renderer);
